import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const WishlistCtx = createContext(null);
export const useWishlist = () => useContext(WishlistCtx);

const keyOf = (productId, vehicleId) => (productId ? `p:${productId}` : `v:${vehicleId}`);

/** Loads the signed-in user's wishlist once and shares it, instead of one request per heart button. */
export function WishlistProvider({ children }) {
    const { user } = useAuth();
    const [keys, setKeys] = useState(() => new Set());

    const refresh = useCallback(async () => {
        if (!user) { setKeys(new Set()); return; }
        try {
            const { data } = await api.get("/wishlist");
            setKeys(new Set(data.map((w) => keyOf(w.product_id, w.vehicle_id))));
        } catch { /* keep the last known state */ }
    }, [user]);

    useEffect(() => { refresh(); }, [refresh]);

    const has = useCallback((productId, vehicleId) => keys.has(keyOf(productId, vehicleId)), [keys]);

    const toggle = useCallback(async (productId, vehicleId) => {
        const k = keyOf(productId, vehicleId);
        if (keys.has(k)) {
            const params = productId ? `product_id=${productId}` : `vehicle_id=${vehicleId}`;
            await api.delete(`/wishlist?${params}`);
            setKeys((prev) => { const n = new Set(prev); n.delete(k); return n; });
            return false;
        }
        await api.post("/wishlist", { product_id: productId, vehicle_id: vehicleId });
        setKeys((prev) => new Set(prev).add(k));
        return true;
    }, [keys]);

    const value = useMemo(() => ({ has, toggle, refresh }), [has, toggle, refresh]);
    return <WishlistCtx.Provider value={value}>{children}</WishlistCtx.Provider>;
}
